<template>
  <picture>
    <template v-for="b in config.breakpoints" :key="b.key">
      <source
        :srcset="`${computedBreakpoints[b.key]}&fm=webp`"
        :media="b.minWidth ? `(min-width: ${b.minWidth})` : undefined"
        type="image/webp"
      />
      <source
        :srcset="computedBreakpoints[b.key]"
        :media="b.minWidth ? `(min-width: ${b.minWidth})` : undefined"
      />
    </template>
    <img
      :class="$style.img"
      :src="computedDefault"
      :loading="eager ? 'eager' : 'lazy'"
      :alt="alt"
    />
  </picture>
</template>

<script setup lang="ts">
import type { TypeResponsiveImageWithoutUnresolvableLinksResponse } from '~/types/contentful'

const props = defineProps<{
  srcset: TypeResponsiveImageWithoutUnresolvableLinksResponse
  alt: string
  quality: number
  query: string
  eager: boolean
}>()

type TypeResponsiveImageBreakpoints =
  | 'default'
  | 'w1024up'
  | 'w1023'
  | 'w767'
  | 'w575'
type TypeBreakpoints = Record<TypeResponsiveImageBreakpoints, string>

const config: {
  defaultKey: TypeResponsiveImageBreakpoints
  breakpoints: {
    key: TypeResponsiveImageBreakpoints
    minWidth?: string
    maxWidthQuery: string
  }[]
} = {
  defaultKey: 'default',
  breakpoints: [
    { key: 'w1024up', minWidth: '1024px', maxWidthQuery: '&w=1920' },
    { key: 'w1023', minWidth: '768px', maxWidthQuery: '&w=1023' },
    { key: 'w767', minWidth: '576px', maxWidthQuery: '&w=767' },
    { key: 'w575', maxWidthQuery: '&w=575' },
  ],
}

const computedDefault = computed(
  () =>
    `${props.srcset?.fields?.[config.defaultKey]?.fields?.file?.url}?q=${
      props.quality || '70'
    }`,
)

const computedBreakpoints = computed(() => {
  return config.breakpoints.reduce<TypeBreakpoints>((acc, cur) => {
    const url = props.srcset?.fields?.[cur.key]?.fields?.file?.url

    return {
      ...acc,
      [cur.key]: url
        ? `${url}?q=${props.quality || '70'}`
        : `${computedDefault.value}${cur.maxWidthQuery}${
            props.query || '&h=900&fit=crop'
          }`,
    }
  }, {} as TypeBreakpoints)
})
</script>

<style lang="scss" module>
.img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: right center;
}
</style>
